/**
 * ProtectedRoute — gates render on the BFF auth boot probe.
 *
 *   loading  → show <Loading/> while the boot GET /auth/me is in flight.
 *   no user  → redirect to /login (with the original path stashed in
 *              location.state so LoginPage can round-trip it).
 *   has user → render children.
 *
 * Under BFF auth there is no silent-refresh flicker (the chores
 * backend refreshes server-side), so the sticky hasBeenAuthenticated
 * flag and the three-way sessionStatus state machine that the
 * pre-BFF version threaded through are gone.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAuthSettled, selectIsAuthenticated } from '../selectors';
import { Loading } from '@/pages/Loading';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const settled = useSelector(selectAuthSettled);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const location = useLocation();

  if (!settled) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
